import { useTranslation } from 'react-i18next'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { useEffect, useState, useContext, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Button, Breadcrumbs, ListItemButton, Typography } from '@mui/material'

import { config } from '../config'
import { countriesCurrencies } from '../lib'
import { Document, FormError, UserSessionType } from '../types'
import { useSocket } from '../hooks/use-socket'
import Dialog from '../components/common/dialog'
import '../styles/document-details-management.css'
import NextIcon from '../assets/icons/next-icon.svg'
import { UserContext } from '../contexts/user.context'
import { AuthContext } from '../contexts/auth.context'
import { getBatchDetailsDataById } from '../api/app-data.api'
import { AppStateContext } from '../contexts/app-state.context'
import { checkTwoFactorAuth, requestAccessToken } from '../api/auth.api'
import { NotificationsContext } from '../contexts/notifications.context'
import { baseButtonWhiteBg, baseButtonBlueBg } from '../theme/custom-styles'
import SectionHeaderData from '../components/document-details-management/section-header-data'
import {downloadProcessedDocument, getDownloadDocumentPreSignedURL, resolveDocumentsAccessToken } from '../api/documents.api'
import DocumentErrorDownloading from '../components/document-details-management/document-error-downloading'
import { checkIfDocumentIsWithNovelties, handleCheckIfOtpCodeIsValid, numberWithCommas } from '../utils/common.utils'
import DocumentFindingsGeneralDetails from '../components/document-details-management/document-findings-general-details'
import { sendPaymentsToDisperse, getAmountToBePaidByDocumentId, discardNoveltiesAndSendToDisperse } from '../api/payments.api'

const DocumentDetailsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const { t } = useTranslation(['document-details-management', 'two-factor-auth', 'common'])

  const location = useLocation()
  const navigate = useNavigate()
  const { documentId } = useParams()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { updateLoadingStatus } = useContext(AppStateContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const componentDidMount = useRef<boolean>(false)
  const dispersingPaymentsRef = useRef<boolean>(false)
  const alreadyRedirectedRef = useRef<boolean>(false)
  const currentDocumentDetailsRef = useRef<Document | Partial<Document> | null>(null)

  const [otpCode, setOtpCode] = useState('')
  const [errors, setErrors] = useState<FormError>({})
  const [documentName, setDocumentName] = useState('')
  const [firstPathLink, setFirstPathLink] = useState('/')
  const [amountToBePaid, setAmountToBePaid] = useState(0)
  const [withNovelties, setWithNovelties] = useState(false)
  const [moduleErrorState, setModuleErrorState] = useState(false)
  const [amountToBePaidByFees, setAmountToBePaidByFees] = useState(0)
  const [hideActionsButtons, setHideActionsButtons] = useState(false)
  const [previousPathLink, setPreviousPathLink] = useState('/documents-to-review')
  const [firstPathText, setFirstPathText] = useState(t('breadcrumbs.batches'))
  const [documentData, setDocumentData] = useState<Document | Partial<Document>>({})
  const [previousPathText, setPreviousPathText] = useState(t('breadcrumbs.thirdLevel'))
  const [dialogConfirmDiscardNoveltiesIsOpen, setDialogConfirmDiscardNoveltiesIsOpen] = useState(false)
  const [dialogConfirmSendToDisperseIsOpen, setDialogConfirmSendToDisperseIsOpen] = useState<boolean>(false)
  const [dialogConfirmDiscardNoveltiesIsLoading, setDialogConfirmDiscardNoveltiesIsLoading] = useState(false)
  const [dialogConfirmSendToDisperseIsLoading, setDialogConfirmSendToDisperseIsLoading] = useState<boolean>(false)

  useEffect(() => {
    handleGetBatchDetailsDataById()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    appDataSocket.on(`updateBatch:${documentId}`, async (dataFromSocket: any) => {
      if (dataFromSocket.deleted === true) {
        navigate('/')
        return
      }

      handleGetBatchDetailsDataByIdFromRemote()
    })

    appDataSocket.on(`deleteBatch:${documentId}`, async () => {
      navigate('/')
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    setFirstPathLink(`/customer-company-batches/${userSession?.belongsTo}`)
    setFirstPathText(t('breadcrumbs.batches'))

    if (location.state?.previousPath && location.state?.previousPath === 'home') {
      setPreviousPathLink('/documents-to-review')
      setFirstPathText(t('breadcrumbs.firstLevel'))
      setPreviousPathText(t('breadcrumbs.thirdLevel'))
    }

    if (location.state?.previousPath && location.state?.previousPath === `customer-company-batches/${userSession?.belongsTo}`) {
      setFirstPathLink(`/`)
      setPreviousPathLink(`/customer-company-batches/${userSession?.belongsTo}`)
      setFirstPathText(t('breadcrumbs.home'))
      setPreviousPathText(t('breadcrumbs.batches'))
    }

    if (documentData.status === 'ready-for-disperse' && location.state?.previousPath !== `customer-company-batches/${userSession?.belongsTo}`) {
      setPreviousPathText(t('breadcrumbs.fourthLevel'))
      setPreviousPathLink('/documents-ready')
    } else if ((documentData.status === 'dispersing' || documentData.status === 'register-disperse-completed') && location.state?.previousPath !== `customer-company-batches/${userSession?.belongsTo}`) {
      setPreviousPathText(t('breadcrumbs.fifthLevel'))
      setPreviousPathLink('/documents-completed')
      setDialogConfirmSendToDisperseIsOpen(false)
      setDialogConfirmSendToDisperseIsLoading(false)
    } else if (location.state?.previousPath !== `customer-company-batches/${userSession?.belongsTo}`) {
      setPreviousPathText(t('breadcrumbs.thirdLevel'))
      setPreviousPathLink('/documents-to-review')
    }

    if (location.state?.previousPath.includes('documents-completed')) {
      setPreviousPathText(t('breadcrumbs.completed'))
      setPreviousPathLink('/documents-completed')
    }

    console.log('location.state?.previousPath', location.state?.previousPath)

    if (documentData) setDocumentName(documentData.originalName ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, documentData, t, userSession])

  const handleGetBatchDetailsDataById = () => {
    let paymentDetailsDataByDocumentIdToSet: any = {}
    const localPaymentDetailsDataByDocumentId = globalThis.sessionStorage.getItem(`batchDetailsById:${documentId}`)

    if (localPaymentDetailsDataByDocumentId) {
      paymentDetailsDataByDocumentIdToSet = JSON.parse(localPaymentDetailsDataByDocumentId)
      handleSetInitialData(paymentDetailsDataByDocumentIdToSet)
    }

    handleGetBatchDetailsDataByIdFromRemote()
  }

  const handleGetBatchDetailsDataByIdFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getBatchDetailsDataById(String(documentId), accessToken)

      handleSetInitialData(data)
      globalThis.sessionStorage.setItem(`batchDetailsById:${documentId}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetPaymentsToReview > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    if (!dataToSet.batchData) {
      navigate('/404')
      return
    }

    if (alreadyRedirectedRef.current === false && dataToSet.batchData?.status === 'completed') {
      // navigate('/documents-completed')
      alreadyRedirectedRef.current = true
      navigate(`/document-payments-details/${documentId}`)
      updateNewNotificationStatus?.(true, 'batch-sent-to-disperse-success')
      return
    }

    if (alreadyRedirectedRef.current === false && (dataToSet.batchData?.status === 'dispersing' || dataToSet.batchData?.status === 'register-disperse-completed')) {
      alreadyRedirectedRef.current = true
      navigate(`/document-payments-details/${documentId}`)
      updateNewNotificationStatus?.(true, 'batch-sent-to-disperse-success')
      return
    }

    if (dataToSet.batchData.status.toLowerCase().indexOf('error') !== -1) {
      setModuleErrorState(true)
    }

    currentDocumentDetailsRef.current = dataToSet.batchData
    const documentWithNovelties = checkIfDocumentIsWithNovelties(dataToSet.batchData)
    setWithNovelties(documentWithNovelties)
    setDocumentData(dataToSet.batchData)

    if (componentDidMount.current === false) {
      componentDidMount.current = true
    }
  }

  const handleDownloadUploadedDocument = async () => {
    updateLoadingStatus?.(true)

    const documentFileSystemName = {
      documentFileSystemName: documentData.fileSystemName ?? ''
    }
    const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
    const documentDownloadPreSignedURLResponse = await getDownloadDocumentPreSignedURL(documentFileSystemName, accessToken)
    window.open(documentDownloadPreSignedURLResponse.data, '_blank')

    updateLoadingStatus?.(false)
  }

  const handleDownloadProcessedDocument = async () => {
    try {
      setErrors({})
      updateLoadingStatus?.(true)
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const processedDocumentDownloaded = await downloadProcessedDocument(documentId ?? '', accessToken)

      window.open(processedDocumentDownloaded.data, '_blank')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }

    updateLoadingStatus?.(false)
  }

  const handleGetAmountToBePaid = async () => {
    try {
      setErrors({})
      updateLoadingStatus?.(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const amountToBePaidByDocumentId = await getAmountToBePaidByDocumentId(documentId ?? '', currency, accessToken)

      setAmountToBePaid(amountToBePaidByDocumentId.data.amountToBePaid)
      // setAmountToBePaidByFees(amountToBePaidByDocumentId.data.amountToBePaidByFees)
      // setAmountToBePaidByFees(6500)
      setAmountToBePaidByFees(amountToBePaidByDocumentId.data.amountToBePaidByFees)
      setDialogConfirmSendToDisperseIsOpen(true)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message, Object.keys(error))
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.insufficientAdamoBalance', { ns: 'common' }) })
      } else if (error?.response?.status === 400) {
        if (error?.response?.data?.balance && error?.response?.data?.amountToBePaid) {
          setErrors({
            generalError: `${t('formGeneralErrors.insufficientBalance', { ns: 'common' })}. ${t('formGeneralErrors.amountToBePaid', { ns: 'common' })} $ ${numberWithCommas(
              error?.response?.data?.amountToBePaid
            )} ${currency.toUpperCase()}. ${t('formGeneralErrors.feesToBePaid', { ns: 'common' })} $ ${numberWithCommas(error?.response?.data?.amountToBePaidByFees)} ${currency.toUpperCase()}. ${t(
              'formGeneralErrors.currentBalance',
              {
                ns: 'common'
              }
            )} $ ${numberWithCommas(error?.response?.data?.balance)} ${currency.toUpperCase()}. ${t('formGeneralErrors.pocketUsed', { ns: 'common' })} ${numberWithCommas(error?.response?.data?.walletName)}`
          })
        } else {
          setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
        }

        setAmountToBePaid(Number(error?.response.data.message.split(':')[1]))
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      updateLoadingStatus?.(false)
    }
  }

  const handleSendPaymentsToDisperse = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setDialogConfirmSendToDisperseIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      return setDialogConfirmSendToDisperseIsLoading(false)
    }

    try {
      setErrors({})
      setOtpCode('')
      setDialogConfirmSendToDisperseIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      dispersingPaymentsRef.current = true
      await sendPaymentsToDisperse(documentId ?? '', accessToken) // --> La respuesta se trae desde el socket

      if (alreadyRedirectedRef.current === false) {
        alreadyRedirectedRef.current = true
        navigate(`/document-payments-details/${documentId}`)
        updateNewNotificationStatus?.(true, 'batch-sent-to-disperse-success')
      }

      // const documentDataUpdated = { ...documentData }
      // documentDataUpdated.status = 'dispersing'
      // setDocumentData(documentDataUpdated)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else if (error?.response?.status === 400) {
        if (error?.response?.data?.balance && error?.response?.data?.amountToBePaid) {
          setErrors({
            generalError: `${t('formGeneralErrors.insufficientBalance', { ns: 'common' })}. ${t('formGeneralErrors.amountToBePaid', { ns: 'common' })} $ ${numberWithCommas(
              error?.response?.data?.amountToBePaid
            )} ${currency.toUpperCase()}. ${t('formGeneralErrors.feesToBePaid', { ns: 'common' })} $ ${numberWithCommas(error?.response?.data?.amountToBePaidByFees)} ${currency.toUpperCase()}. ${t(
              'formGeneralErrors.currentBalance',
              {
                ns: 'common'
              }
            )} $ ${numberWithCommas(error?.response?.data?.balance)} ${currency.toUpperCase()}. ${t('formGeneralErrors.pocketUsed', { ns: 'common' })} ${numberWithCommas(error?.response?.data?.walletName)}`
          })
        } else {
          setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
        }

        setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.insufficientAdamoBalance', { ns: 'common' }) })
      } else if (error?.response?.status === 504) {
        const documentDataUpdated = { ...documentData }
        documentDataUpdated.status = 'dispersing'
        setDocumentData(documentDataUpdated)
      } else {
        const documentDataUpdated = { ...documentData }
        documentDataUpdated.status = 'dispersing'
        setDocumentData(documentDataUpdated)
        // setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setHideActionsButtons(true)
    }

    setDialogConfirmSendToDisperseIsOpen(false)
    setDialogConfirmSendToDisperseIsLoading(false)
    setTimeout(() => updateLoadingStatus?.(false), 2000)
  }

  const handleDiscartDocumentNoveltiesAndPrepareForDisperse = async () => {
    try {
      setErrors({})
      setDialogConfirmDiscardNoveltiesIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await discardNoveltiesAndSendToDisperse(documentId ?? '', accessToken)

      handleGetBatchDetailsDataById()
      setDialogConfirmDiscardNoveltiesIsOpen(false)
      updateNewNotificationStatus?.(true, 'document-ready-for-disperse')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else if (error?.response?.status === 400) {
        setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }

    setDialogConfirmDiscardNoveltiesIsLoading(false)
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="document-details-section-container">
      <div
        className={
          !documentData?.status
            ? 'document-details-section-header document-details-section-header-loading'
            : moduleErrorState
            ? 'document-details-section-header-error'
            : documentData?.status === 'dispersing' || documentData?.status === 'register-disperse-completed'
            ? 'document-details-section-header document-details-section-header-dispersing'
            : 'document-details-section-header document-details-section-header-success'
        }
      >
        <Breadcrumbs
          separator={
            <img
              src={NextIcon}
              alt="next-icon"
              className="next-icon"
            />
          }
          sx={{ width: '100%' }}
          aria-label="breadcrumb"
        >
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate(firstPathLink)}
          >
            <Typography
              key="3"
              className={moduleErrorState ? 'breadcrumb-inactive-error' : 'breadcrumb-inactive-success'}
            >
              {firstPathText}
            </Typography>
          </ListItemButton>
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate(previousPathLink)}
          >
            <Typography
              key="3"
              className={moduleErrorState ? 'breadcrumb-inactive-error' : 'breadcrumb-inactive-success'}
            >
              {previousPathText}
            </Typography>
          </ListItemButton>
          <Typography
            key="3"
            className={moduleErrorState ? 'breadcrumb-active-error' : 'breadcrumb-active-success'}
          >
            {documentName}
          </Typography>
        </Breadcrumbs>
        <SectionHeaderData
          sectionTitle={
            moduleErrorState
              ? t('sectionHeaderData.titles.errorStatus')
              : withNovelties
              ? t('sectionHeaderData.titles.findingsStatus')
              : documentData?.status && documentData?.status === 'ready-for-disperse'
              ? t('sectionHeaderData.titles.readyStatus')
              : documentData?.status && (documentData?.status === 'dispersing' || documentData?.status === 'register-disperse-completed')
              ? t('sectionHeaderData.titles.dispersingStatus')
              : t('sectionHeaderData.titles.loadingStatus')
          }
          sectionTitleType={
            moduleErrorState
              ? t('sectionHeaderData.subtitles.errorStatus')
              : !documentData?.status
              ? t('sectionHeaderData.subtitles.loadingStatus')
              : documentData?.status && (documentData?.status === 'dispersing' || documentData?.status === 'register-disperse-completed')
              ? t('sectionHeaderData.subtitles.dispersingStatus')
              : documentData?.status &&
                documentData?.status === 'ready-for-disperse' &&
                Number(documentData?.totalOfNoveltiesReviewed) < Number(documentData?.totalOfRegistries) &&
                Number(documentData?.totalOfNoveltiesReviewed) > 0
              ? t('sectionHeaderData.subtitles.successStatusConditional', { toBeDispersed: Number(documentData?.totalOfNoveltiesReviewed), sentToReview: Number(documentData?.totalOfNoveltiesRejected) })
              : documentData?.status &&
                documentData?.status === 'ready-for-disperse' &&
                Number(documentData?.totalOfNoveltiesReviewed) === Number(documentData?.totalOfRegistries) &&
                Number(documentData?.totalOfNoveltiesReviewed)
              ? t('sectionHeaderData.subtitles.successStatus', { totalOfRegistries: documentData?.totalOfRegistries })
              : ''
          }
        />
      </div>
      <div className="document-details-content-container">
        {moduleErrorState && (
          <div className="document-details-content">
            <DocumentErrorDownloading
              documentData={documentData}
              handleDownloadUploadedDocument={handleDownloadUploadedDocument}
            />
          </div>
        )}
        {withNovelties && (
          <div className="document-details-content">
            <DocumentFindingsGeneralDetails
              userSession={userSession as UserSessionType}
              documentData={documentData}
            />
          </div>
        )}
      </div>
      {moduleErrorState && (
        <div className="document-details-actions-container">
          <div className="document-details-actions-content">
            <Button
              variant="contained"
              sx={baseButtonWhiteBg}
              onClick={() => navigate('/documents-to-review')}
            >
              {t('documentErrorDownloading.actionButtonText')}
            </Button>
          </div>
        </div>
      )}
      {withNovelties && (
        <div
          style={{ marginTop: '40px' }}
          className="document-details-actions-container"
        >
          <div className="document-details-actions-content">
            {Number(documentData.totalOfNoveltiesReviewed) > 0 && (
              <Button
                variant="text"
                sx={baseButtonBlueBg}
                onClick={() => setDialogConfirmDiscardNoveltiesIsOpen(true)}
              >
                {t('documentFindingsGeneralDetails.actionDiscartButtonText')}
              </Button>
            )}
            <Button
              variant="contained"
              sx={baseButtonWhiteBg}
              onClick={() => navigate(`/document-novelties-details/${String(documentData._id)}`)}
            >
              {t('documentFindingsGeneralDetails.actionButtonText')}
            </Button>
          </div>
        </div>
      )}
      {documentData.status === 'ready-for-disperse' && (
        <div className="document-details-actions-container">
          {errors?.generalError && (
            <div
              className="error-message"
              style={{ margin: '0px 0px 40px', maxWidth: '1000px', padding: '0px 20px', boxSizing: 'border-box' }}
            >
              {errors?.generalError}
            </div>
          )}
          <div className="document-details-actions-content">
            <Button
              variant="contained"
              sx={baseButtonBlueBg}
              onClick={handleDownloadProcessedDocument}
            >
              {t('documentReadyForDisperse.actionButtonText.downloadProcessedDocument')}
            </Button>
            {(userSession?.role === 'admin' ||
              userSession?.role === 'super-admin' ||
              (userSession?.role === 'admin-client' && userSession?.belongsTo !== config.app.isliveCompanyId) ||
              userSession?.role === 'super-admin-client') && (
              <Button
                variant="contained"
                sx={baseButtonWhiteBg}
                onClick={handleGetAmountToBePaid}
              >
                {t('documentReadyForDisperse.actionButtonText.dispersePaymentsNow')}
              </Button>
            )}
          </div>
        </div>
      )}
      {(documentData.status === 'dispersing' || documentData.status === 'register-disperse-completed') && (
        <div className="document-details-actions-container">
          <div className="document-details-actions-content">
            <Button
              variant="contained"
              sx={baseButtonWhiteBg}
              onClick={() => navigate('/documents-completed')}
            >
              {t('documentDispersing.actionButtonText')}
            </Button>
          </div>
        </div>
      )}
      {dialogConfirmDiscardNoveltiesIsOpen && (
        <Dialog
          errors={errors}
          dialogTitle={t('discardNoveltiesToDisperse.title')}
          dialogIsLoading={dialogConfirmDiscardNoveltiesIsLoading}
          actionButtonText={t('discardNoveltiesToDisperse.actionButtonText')}
          handleDialogAction={handleDiscartDocumentNoveltiesAndPrepareForDisperse}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
            }

            setDialogConfirmDiscardNoveltiesIsOpen(openState)
          }}
        >
          <div className="dialog-content-text">
            <div>{t('discardNoveltiesToDisperse.text')}</div>
          </div>
        </Dialog>
      )}
      {dialogConfirmSendToDisperseIsOpen && (
        <Dialog
          errors={errors}
          hideActionsButtons={hideActionsButtons}
          dialogTitle={t('confirmSendToDisperse.title')}
          handleDialogAction={handleSendPaymentsToDisperse}
          dialogIsLoading={dialogConfirmSendToDisperseIsLoading}
          actionButtonText={t('confirmSendToDisperse.actionButtonText')}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
            }

            updateLoadingStatus?.(false)
            setDialogConfirmSendToDisperseIsOpen(openState)
          }}
        >
          <div className="dialog-content-text">
            <div>{t('confirmSendToDisperse.text')}</div>
            <div style={{ fontWeight: 'bold', color: '#107569' }}>{t('confirmSendToDisperse.amountText', { amountToBePaidText: `$ ${numberWithCommas(Number(amountToBePaid))} ${currency.toUpperCase()}` })}</div>
            <div
              className="step"
              style={{ marginTop: '0px', gap: '16px' }}
            >
              <div
                className="make-individual-payment-form-personal-data-title"
                style={{ padding: '0px 20px', boxSizing: 'border-box', fontSize: '12px', lineHeight: '150%', color: '#EF4444', fontWeight: '600' }}
              >
                {t('confirmSendToDisperse.warning', { amountToBePaidByFeesText: `$ ${numberWithCommas(Number(amountToBePaidByFees))} ${currency.toUpperCase()}` })}
              </div>
              <div
                className="step-instruction-container"
                style={{ fontSize: '14px', color: '#F59E0B' }}
              >
                {t('checkCodeInstruction', { ns: 'two-factor-auth' })}
              </div>
              <MuiOtpInput
                length={6}
                value={otpCode}
                onChange={(newValue: string) => setOtpCode(newValue)}
                className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
              />
            </div>
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default DocumentDetailsManagement
